'use client';

// SPECS: __tests__/use-sse-lifecycle.test.tsx

import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useSSE — React hook wrapping the EventSource API for Server-Sent Events.
 *
 * Features:
 * - Automatic reconnection with exponential backoff (1s → 2s → 4s → ... → 30s max)
 * - Fallback to polling if SSE fails 3 consecutive times
 * - Pauses SSE connection when tab is hidden, reconnects when visible
 * - Returns `{ data, connected, error, lastUpdated }`
 *
 * @param url        SSE endpoint URL (e.g., `/api/sse/tasks`)
 * @param eventName  SSE event name to listen for (e.g., `tasks`)
 * @param options    Optional configuration
 */

interface UseSSEOptions<T> {
  /** Called when a message is received (alternative to reading `data`) */
  onMessage?: (_data: T) => void;
  /** Polling fallback URL — if not set, uses the same `url` via regular fetch */
  fallbackUrl?: string;
  /** Polling interval in ms when falling back (default: 5000) */
  fallbackIntervalMs?: number;
  /** Transform function for polling fallback response */
  fallbackTransform?: (_json: Record<string, unknown>) => T;
  /** Max consecutive SSE failures before switching to polling fallback (default: 3) */
  maxRetries?: number;
  /** Whether to pause when not needed (e.g., panel closed) */
  enabled?: boolean;
}

interface UseSSEResult<T> {
  /** Latest data received from SSE or polling fallback */
  data: T | null;
  /** Whether the SSE connection is currently open */
  connected: boolean;
  /** Last error message, if any */
  error: string | null;
  /** Timestamp of last successful data receipt */
  lastUpdated: Date | null;
  /** Whether currently using polling fallback instead of SSE */
  usingFallback: boolean;
}

export function useSSE<T>(
  url: string | null,
  eventName: string,
  options: UseSSEOptions<T> = {},
): UseSSEResult<T> {
  const {
    onMessage,
    fallbackUrl,
    fallbackIntervalMs = 5000,
    fallbackTransform,
    maxRetries = 3,
    enabled = true,
  } = options;

  const [data, setData] = useState<T | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);

  // Refs for stable callback access and cleanup
  const esRef = useRef<EventSource | null>(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const fallbackTransformRef = useRef(fallbackTransform);
  fallbackTransformRef.current = fallbackTransform;

  const retryCountRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fallbackIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fallbackRequestRef = useRef<AbortController | null>(null);
  const isVisibleRef = useRef(typeof document !== 'undefined' ? document.visibilityState === 'visible' : true);
  const mountedRef = useRef(false);
  const generationRef = useRef(0);

  // Stable message handler
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const parsed = JSON.parse(event.data) as T;
      if (!mountedRef.current) return;
      setData(parsed);
      setLastUpdated(new Date());
      setError(null);
      retryCountRef.current = 0; // Reset on success
      onMessageRef.current?.(parsed);
    } catch {
      // Ignore malformed messages
    }
  }, []);

  // Start polling fallback
  const startFallback = useCallback(() => {
    if (!mountedRef.current) return;
    const pollUrl = fallbackUrl || url;
    if (!pollUrl) return;
    const generation = generationRef.current;

    setUsingFallback(true);
    setConnected(false);

    // Clear any existing fallback interval
    if (fallbackIntervalRef.current) {
      clearInterval(fallbackIntervalRef.current);
    }
    fallbackRequestRef.current?.abort();
    fallbackRequestRef.current = null;

    const poll = async () => {
      if (!mountedRef.current || generationRef.current !== generation || !isVisibleRef.current) return;
      // Skip if a prior poll is still outstanding — a slow request must not
      // pile up additional in-flight fetches while the interval keeps ticking.
      if (fallbackRequestRef.current) return;
      const request = new AbortController();
      fallbackRequestRef.current = request;
      const isCurrent = () => mountedRef.current && generationRef.current === generation
        && fallbackRequestRef.current === request && !request.signal.aborted;
      try {
        const res = await fetch(pollUrl, { signal: request.signal });
        if (!isCurrent()) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!isCurrent()) return;
        const result = fallbackTransformRef.current ? fallbackTransformRef.current(json) : json as T;
        setData(result);
        setLastUpdated(new Date());
        setError(null);
        onMessageRef.current?.(result);
      } catch (err) {
        if (!isCurrent()) return;
        setError(err instanceof Error ? err.message : 'Polling failed');
      } finally {
        // An obsolete request must never release a replacement poll's guard.
        if (fallbackRequestRef.current === request) fallbackRequestRef.current = null;
      }
    };

    // Immediate first poll
    void poll();
    fallbackIntervalRef.current = setInterval(poll, fallbackIntervalMs);
  }, [url, fallbackUrl, fallbackIntervalMs]);

  // Stop polling fallback
  const stopFallback = useCallback(() => {
    if (fallbackIntervalRef.current) {
      clearInterval(fallbackIntervalRef.current);
      fallbackIntervalRef.current = null;
    }
    fallbackRequestRef.current?.abort();
    fallbackRequestRef.current = null;
    setUsingFallback(false);
  }, []);

  // Connect to SSE
  const connect = useCallback(() => {
    if (!url || !mountedRef.current || !isVisibleRef.current) return;

    // Close existing connection
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    // Clear reconnect timer
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    const es = new EventSource(url);
    esRef.current = es;
    const generation = generationRef.current;
    const isCurrent = () => mountedRef.current && generationRef.current === generation && esRef.current === es;

    es.addEventListener(eventName, (event: MessageEvent) => {
      if (isCurrent()) handleMessage(event);
    });

    // Listen for server-side error reports (persistent data fetch failures)
    es.addEventListener('error-report', (event: MessageEvent) => {
      if (!isCurrent()) return;
      try {
        const { message } = JSON.parse(event.data) as { channel: string; message: string };
        setError(message || 'Data source unavailable');
        setConnected(false);
      } catch { /* ignore malformed */ }
    });

    es.onopen = () => {
      if (!isCurrent()) return;
      setConnected(true);
      setError(null);
      retryCountRef.current = 0;
      // If we were using fallback, stop it
      stopFallback();
    };

    es.onerror = () => {
      if (!isCurrent()) return;
      setConnected(false);
      es.close();
      esRef.current = null;

      retryCountRef.current += 1;

      if (retryCountRef.current >= maxRetries) {
        // Switch to polling fallback
        setError(`SSE failed ${maxRetries} times, falling back to polling`);
        startFallback();
        return;
      }

      // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
      const delay = Math.min(1000 * Math.pow(2, retryCountRef.current - 1), 30000);
      setError(`Connection lost, reconnecting in ${Math.round(delay / 1000)}s...`);
      reconnectTimerRef.current = setTimeout(() => {
        if (mountedRef.current && generationRef.current === generation && isVisibleRef.current) {
          connect();
        }
      }, delay);
    };
  }, [url, eventName, handleMessage, maxRetries, startFallback, stopFallback]);

  // Disconnect SSE
  const disconnect = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    setConnected(false);
  }, []);

  // Main effect: manage SSE connection lifecycle
  useEffect(() => {
    generationRef.current += 1;
    mountedRef.current = enabled && !!url;
    retryCountRef.current = 0;

    if (!enabled || !url) {
      disconnect();
      stopFallback();
      return;
    }

    connect();

    return () => {
      mountedRef.current = false;
      disconnect();
      stopFallback();
    };
  }, [url, enabled, connect, disconnect, stopFallback]);

  // Visibility handling: pause SSE when tab is hidden, reconnect when visible
  useEffect(() => {
    if (!enabled || !url) return;

    const handleVisibility = () => {
      isVisibleRef.current = document.visibilityState === 'visible';

      if (isVisibleRef.current) {
        // Tab became visible — reconnect SSE (or restart fallback polling)
        if (usingFallback) {
          startFallback();
        } else {
          connect();
        }
      } else {
        // Tab hidden — disconnect to save resources
        disconnect();
        stopFallback();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [url, enabled, usingFallback, connect, disconnect, startFallback, stopFallback]);

  return { data, connected, error, lastUpdated, usingFallback };
}
